import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Upload,
  AlertCircle,
  Plane,
  Globe,
  Users,
  Calendar,
  Mic2,
  Heart,
  Camera,
  Truck,
  Sparkles,
  Bike,
  Star,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HERO_IMG = "/booked-outside-hero.png";

const INTEREST_OPTIONS = [
  "Becoming a travel agent",
  "Building my brand",
  "Luxury travel",
  "Couples travel",
  "Group travel",
  "Content creation",
  "Networking",
  "Reality TV exposure",
];

const ROLES = [
  { label: "Aspiring Travel Agents", Icon: Plane, blurb: "Learn the trade. Get booked. Get paid." },
  { label: "Travel Creators", Icon: Camera, blurb: "Document the journey. Build a following." },
  { label: "Couples Who Love Travel", Icon: Heart, blurb: "Travel together, build together." },
  { label: "Motorcycle Culture", Icon: Bike, blurb: "Bikers who ride to live and live to travel." },
  { label: "Truckers Who Travel", Icon: Truck, blurb: "Long-haul lifers chasing more than miles." },
  { label: "Influencers", Icon: Mic2, blurb: "Audience-builders ready for the next level." },
  { label: "Luxury Lifestyle", Icon: Star, blurb: "Champagne flights, suites, soft-life energy." },
  { label: "Event Hosts", Icon: Calendar, blurb: "If you can light a room, you can light a trip." },
  { label: "Entrepreneurs", Icon: Sparkles, blurb: "Travel income, travel businesses, travel brands." },
  { label: "Social Media Creators", Icon: Users, blurb: "Build your brand from the runway up." },
];

const WHY_BULLETS = [
  "Learn from experienced travel entrepreneurs",
  "Be featured on an AutoMotoVille Original",
  "Build your personal brand",
  "Travel with the culture",
  "Network with creators and entrepreneurs",
  "Access destination experiences",
  "Participate in group trips and events",
  "Grow your social media audience",
  "Create content while traveling",
  "Experience luxury and culture together",
];

const STEPS = [
  { key: "about", title: "About You" },
  { key: "experience", title: "Travel Background" },
  { key: "ready", title: "This Is More Than a Vacation" },
  { key: "media", title: "Photos & Intro Video" },
];

const DEFAULT_FORM = {
  full_name: "",
  age: "",
  city: "",
  state: "",
  phone: "",
  email: "",
  socials: "",
  is_travel_agent: "",
  why_join: "",
  reality_tv_fit: "",
  traveled_before: "",
  comfortable_on_camera: "",
  interested_in: [],
  q_training_willing: "",
  q_serious_about_business: "",
  q_camera_active: "",
  q_open_to_networking: "",
  q_commitment_level: "",
  video_url: "",
};

const AUTOSAVE_KEY = "booked-outside-form-v1";

export default function BookedOutside() {
  const [step, setStep] = useState(-1); // -1 = before form, 0..3 = steps, 4 = thanks
  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      return raw ? { ...DEFAULT_FORM, ...JSON.parse(raw) } : DEFAULT_FORM;
    } catch {
      return DEFAULT_FORM;
    }
  });
  const [photos, setPhotos] = useState([]); // {dataUrl}
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(form));
    } catch {
      /* ignore */
    }
  }, [form]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleInterest = (k) =>
    update({
      interested_in: form.interested_in.includes(k)
        ? form.interested_in.filter((x) => x !== k)
        : [...form.interested_in, k],
    });

  const scrollToForm = () => {
    setStep((s) => (s < 0 ? 0 : s));
    setTimeout(() => {
      document.getElementById("booked-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const validateStep = () => {
    if (step === 0) {
      const required = ["full_name", "age", "city", "state", "phone", "email"];
      for (const k of required) {
        if (!form[k]?.toString().trim()) {
          setError("Please complete all required fields before continuing.");
          return false;
        }
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        setError("Please enter a valid email address.");
        return false;
      }
    }
    if (step === 1) {
      if (!form.is_travel_agent) {
        setError("Let us know if you're currently a travel agent.");
        return false;
      }
      if (!form.why_join.trim() || form.why_join.trim().length < 20) {
        setError("Please tell us why you want to join (20+ characters).");
        return false;
      }
      if (!form.reality_tv_fit.trim() || form.reality_tv_fit.trim().length < 20) {
        setError("Tell us what makes you good for reality TV (20+ characters).");
        return false;
      }
      if (!form.traveled_before) {
        setError("Let us know if you've traveled before.");
        return false;
      }
      if (!form.comfortable_on_camera) {
        setError("Let us know if you're comfortable being filmed.");
        return false;
      }
      if (form.interested_in.length === 0) {
        setError("Pick at least one Interested In option.");
        return false;
      }
    }
    if (step === 2) {
      const needed = [
        "q_training_willing",
        "q_serious_about_business",
        "q_camera_active",
        "q_open_to_networking",
        "q_commitment_level",
      ];
      for (const k of needed) {
        if (!form[k]) {
          setError("Please answer every readiness question to continue.");
          return false;
        }
      }
    }
    setError("");
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: document.getElementById("booked-form")?.offsetTop || 0, behavior: "smooth" });
  };
  const back = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    if (photos.length < 1) {
      setError("Please upload at least one photo before submitting.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        data: {
          age: form.age,
          socials: form.socials,
          is_travel_agent: form.is_travel_agent,
          why_join: form.why_join,
          reality_tv_fit: form.reality_tv_fit,
          traveled_before: form.traveled_before,
          comfortable_on_camera: form.comfortable_on_camera,
          interested_in: form.interested_in,
          q_training_willing: form.q_training_willing,
          q_serious_about_business: form.q_serious_about_business,
          q_camera_active: form.q_camera_active,
          q_open_to_networking: form.q_open_to_networking,
          q_commitment_level: form.q_commitment_level,
          video_url: form.video_url,
        },
        photos: photos.map((p) => p.dataUrl),
      };
      await axios.post(`${API}/intake/booked-outside`, payload);
      try {
        localStorage.removeItem(AUTOSAVE_KEY);
      } catch {
        /* ignore */
      }
      setStep(STEPS.length); // thank you
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(
        e?.response?.data?.detail?.[0]?.msg || "Could not submit. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#06060a] text-white booked-outside">
      <Hero onApply={scrollToForm} onLearn={() => document.getElementById("about-show")?.scrollIntoView({ behavior: "smooth" })} />
      <AboutShow />
      <RolesSection />
      <WhyJoin />

      <section id="booked-form" className="relative px-6 py-24 md:py-28">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 25%, rgba(255,45,156,0.20), transparent 45%), radial-gradient(circle at 85% 75%, rgba(80,160,255,0.20), transparent 50%)",
          }}
          aria-hidden
        />
        <div className="relative max-w-3xl mx-auto">
          {step >= STEPS.length ? (
            <ThankYou />
          ) : step < 0 ? (
            <ApplyTeaser onApply={scrollToForm} />
          ) : (
            <>
              <div className="font-accent text-[11px] tracking-[0.28em] text-[#FF85C0] mb-3">
                / 06 — CASTING APPLICATION · STEP {step + 1} / {STEPS.length}
              </div>
              <h2 className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight">
                {STEPS[step].title}
              </h2>
              <div className="mt-5 h-[2px] w-full bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] transition-all"
                  style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                />
              </div>

              <div className="mt-10 space-y-5">
                {step === 0 && <StepAbout form={form} update={update} />}
                {step === 1 && (
                  <StepExperience
                    form={form}
                    update={update}
                    toggleInterest={toggleInterest}
                  />
                )}
                {step === 2 && <StepReady form={form} update={update} />}
                {step === 3 && (
                  <StepMedia
                    form={form}
                    update={update}
                    photos={photos}
                    setPhotos={setPhotos}
                  />
                )}
              </div>

              {error && (
                <div
                  className="mt-6 flex items-start gap-2 text-[#FF6B9C] text-sm"
                  data-testid="booked-form-err"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="mt-10 flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  data-testid="booked-back"
                  className="inline-flex items-center gap-2 font-accent text-[11px] tracking-[0.28em] text-white/55 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" /> BACK
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="booked-next"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:scale-[1.03] transition-transform"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="booked-submit"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:scale-[1.03] transition-transform disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Start My Journey <Sparkles className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <FinalCTA onApply={scrollToForm} />
      <Footer />

      {/* Sticky mobile apply */}
      <div className="sm:hidden fixed bottom-4 left-4 right-4 z-50">
        <button
          type="button"
          onClick={scrollToForm}
          data-testid="booked-sticky-apply"
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] text-white font-black px-6 py-3.5 uppercase tracking-[0.28em] text-xs shadow-[0_18px_50px_-12px_rgba(255,45,156,0.65)]"
        >
          Apply Now <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Hero
// ============================================================
function Hero({ onApply, onLearn }) {
  return (
    <section
      className="relative w-full overflow-hidden"
      data-testid="booked-hero"
      style={{ minHeight: "100vh" }}
    >
      {/* Full-bleed hero image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
        aria-hidden
      />
      {/* Cinematic readability gradient — preserves image but boosts text */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(6,6,10,0.25) 0%, rgba(6,6,10,0.05) 40%, rgba(6,6,10,0.55) 80%, rgba(6,6,10,0.92) 100%)",
        }}
        aria-hidden
      />
      {/* Soft side neon vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,45,156,0.10) 0%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 65%, rgba(91,168,255,0.10) 100%)",
        }}
        aria-hidden
      />

      <div className="relative z-10 min-h-screen flex flex-col justify-between max-w-7xl mx-auto px-6 pt-24 sm:pt-28 pb-24">
        {/* Top right back link */}
        <div className="flex justify-between items-start">
          <a
            href="/"
            data-testid="booked-back-home"
            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 bg-black/40 backdrop-blur-sm text-white/85 font-accent text-[10px] tracking-[0.28em] hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> AUTOMOTOVILLE
          </a>
          <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF2D9C]/55 bg-black/40 backdrop-blur-sm font-accent text-[10px] tracking-[0.32em] text-[#FF85C0]">
            NOW CASTING — AUTOMOTOVILLE ORIGINAL
          </div>
        </div>

        {/* Center copy */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-[#FF85C0] mb-3 uppercase">
            A new culture-driven travel reality series
          </div>
          <h1
            className="font-heading uppercase font-black leading-[0.92] tracking-tight text-5xl sm:text-7xl md:text-8xl"
            style={{
              color: "#F6EBD4",
              textShadow:
                "0 4px 32px rgba(0,0,0,0.55), 0 0 1px rgba(255,255,255,0.5)",
            }}
          >
            Booked &{" "}
            <span
              style={{
                background: "linear-gradient(90deg,#FF2D9C 0%,#FF85C0 100%)",
                WebkitBackgroundClip: "text",
                color: "transparent",
                backgroundClip: "text",
              }}
            >
              Outside
            </span>
          </h1>
          <div className="mt-5 font-heading uppercase font-black tracking-[0.18em] text-lg sm:text-2xl text-white/90">
            The culture travels{" "}
            <span style={{ color: "#FF85C0" }}>different.</span>
          </div>
          <p className="mt-6 max-w-2xl text-white/80 leading-relaxed text-sm sm:text-base">
            A new culture-driven travel reality series following aspiring
            travel agents, creators, entrepreneurs, bikers, truckers,
            influencers, and personalities as they learn the travel business,
            build their brands, travel the world, and experience the
            lifestyle while being filmed.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onApply}
              data-testid="booked-hero-apply"
              className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] text-white font-black px-8 py-4 uppercase tracking-[0.28em] text-sm transition-all hover:scale-[1.03] shadow-[0_18px_50px_-15px_rgba(255,45,156,0.65)]"
            >
              Apply Now <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              type="button"
              onClick={onLearn}
              data-testid="booked-hero-learn"
              className="inline-flex items-center justify-center gap-2 border border-white/30 bg-white/[0.06] backdrop-blur-md text-white font-black px-8 py-4 uppercase tracking-[0.28em] text-sm hover:bg-white/10 transition-colors"
            >
              Learn More
            </button>
          </div>
        </motion.div>

        {/* Bottom feature row */}
        <div className="hidden sm:flex items-center gap-8 text-white/80 font-accent text-[11px] tracking-[0.28em] uppercase">
          <span className="inline-flex items-center gap-2"><Plane className="w-4 h-4 text-[#FF85C0]" /> Travel.</span>
          <span className="inline-flex items-center gap-2"><Globe className="w-4 h-4 text-[#5BA8FF]" /> Culture.</span>
          <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-[#FF85C0]" /> Experiences.</span>
          <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-[#5BA8FF]" /> Lifestyle.</span>
        </div>
      </div>
    </section>
  );
}

function ApplyTeaser({ onApply }) {
  return (
    <div className="text-center" data-testid="booked-form-teaser">
      <div className="font-accent text-[11px] tracking-[0.28em] text-[#FF85C0] mb-3">
        / 06 — APPLY
      </div>
      <h2 className="font-heading text-3xl md:text-5xl font-black uppercase">
        Apply for Booked & Outside
      </h2>
      <p className="mt-4 text-white/70 max-w-xl mx-auto">
        Ready to become a travel entrepreneur while being featured on a
        reality series?
      </p>
      <button
        type="button"
        onClick={onApply}
        data-testid="booked-teaser-start"
        className="mt-7 inline-flex items-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs"
      >
        Start My Journey <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================
// What is Booked & Outside
// ============================================================
function AboutShow() {
  return (
    <section
      id="about-show"
      className="relative px-6 py-24 md:py-28 overflow-hidden"
      data-testid="booked-about"
    >
      {/* Flight-path graphic */}
      <svg
        className="absolute inset-0 w-full h-full opacity-25 pointer-events-none"
        viewBox="0 0 1200 600"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="bo-flight" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FF2D9C" stopOpacity="0" />
            <stop offset="50%" stopColor="#FF2D9C" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#5BA8FF" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        <motion.path
          d="M 0 500 Q 300 100 600 300 T 1200 100"
          stroke="url(#bo-flight)"
          strokeWidth="1.5"
          strokeDasharray="6 8"
          fill="transparent"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.2, ease: "easeOut" }}
        />
      </svg>

      <div className="relative max-w-4xl mx-auto">
        <div className="font-accent text-[11px] tracking-[0.28em] text-[#5BA8FF] mb-3">
          / 02 — THE SERIES
        </div>
        <h2
          className="font-heading uppercase font-black leading-[0.95] tracking-tight text-4xl md:text-6xl"
          style={{ color: "#F6EBD4" }}
        >
          What is Booked &{" "}
          <span
            style={{
              background: "linear-gradient(90deg,#FF2D9C 0%,#FF85C0 100%)",
              WebkitBackgroundClip: "text",
              color: "transparent",
              backgroundClip: "text",
            }}
          >
            Outside?
          </span>
        </h2>
        <div className="mt-7 space-y-4 text-white/80 leading-relaxed text-[15px]">
          <p>
            <strong className="text-white">Booked &amp; Outside</strong> is
            more than a travel show. It's a movement built for the culture.
          </p>
          <p>
            We're looking for aspiring travel agents, creators, entrepreneurs,
            bikers, truckers, couples, influencers, and personalities ready
            to learn the travel business while being filmed during their
            journey.
          </p>
          <p>
            From sign-up and training to destination trips, networking
            events, group travel, nightlife experiences, cruises, and content
            creation — viewers will watch the real process unfold.
          </p>
        </div>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-white/75 text-sm">
          {[
            "become a travel agent",
            "build travel income",
            "host trips",
            "become a travel influencer",
            "grow your social media brand",
            "network with like-minded entrepreneurs",
            "or simply experience the lifestyle…",
          ].map((x) => (
            <div key={x} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF2D9C]" /> {x}
            </div>
          ))}
        </div>
        <p className="mt-6 text-white/65 italic">
          This platform gives you the opportunity to build your brand while
          seeing the world.
        </p>
      </div>
    </section>
  );
}

// ============================================================
// Who we're looking for
// ============================================================
function RolesSection() {
  return (
    <section className="relative px-6 py-24 md:py-28" data-testid="booked-roles">
      <div className="max-w-6xl mx-auto">
        <div className="font-accent text-[11px] tracking-[0.28em] text-[#FF85C0] mb-3">
          / 03 — CASTING CALL
        </div>
        <h2
          className="font-heading uppercase font-black leading-[0.95] tracking-tight text-4xl md:text-6xl"
          style={{ color: "#F6EBD4" }}
        >
          Who we're <br />
          looking for.
        </h2>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {ROLES.map((r, i) => (
            <motion.div
              key={r.label}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 5) * 0.06 }}
              className="group relative border border-white/10 bg-white/[0.03] backdrop-blur-md p-4 sm:p-5 overflow-hidden hover:border-[#FF85C0]/60 hover:bg-white/[0.06] transition-all duration-500 hover:shadow-[0_12px_36px_-12px_rgba(255,45,156,0.4)]"
              data-testid={`booked-role-${i}`}
            >
              <div
                className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-30 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(255,45,156,0.45), transparent 65%)",
                }}
              />
              <span className="relative w-9 h-9 border border-[#FF2D9C]/40 bg-black/40 flex items-center justify-center text-[#FF85C0] mb-3">
                <r.Icon className="w-4 h-4" />
              </span>
              <div className="font-heading font-black text-sm md:text-base leading-tight">
                {r.label}
              </div>
              <div className="mt-1 text-[11px] text-white/55 leading-snug">
                {r.blurb}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Why join
// ============================================================
function WhyJoin() {
  return (
    <section
      className="relative px-6 py-24 md:py-28 overflow-hidden"
      data-testid="booked-why"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(255,45,156,0.18), transparent 45%), radial-gradient(circle at 80% 70%, rgba(91,168,255,0.18), transparent 50%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <div className="font-accent text-[11px] tracking-[0.28em] text-[#5BA8FF] mb-3">
            / 04 — WHY JOIN
          </div>
          <h2
            className="font-heading uppercase font-black leading-[0.95] tracking-tight text-4xl md:text-5xl"
            style={{ color: "#F6EBD4" }}
          >
            Build your brand <br />
            while seeing <br />
            <span
              style={{
                background: "linear-gradient(90deg,#FF2D9C 0%,#5BA8FF 100%)",
                WebkitBackgroundClip: "text",
                color: "transparent",
                backgroundClip: "text",
              }}
            >
              the world.
            </span>
          </h2>
        </div>
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {WHY_BULLETS.map((b, i) => (
            <motion.div
              key={b}
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
              className="flex items-start gap-3 border border-white/10 bg-white/[0.03] backdrop-blur-md px-4 py-3"
            >
              <CheckCircle2 className="w-4 h-4 text-[#FF85C0] mt-0.5 shrink-0" />
              <span className="text-sm text-white/80 leading-relaxed">
                {b}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Form steps
// ============================================================
function Field({ label, required, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="font-accent text-[10px] tracking-[0.28em] text-white/55 mb-1.5">
        {label}
        {required && <span className="text-[#FF85C0]"> *</span>}
      </div>
      {children}
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2.5 text-sm bg-black/40 border border-white/10 focus:border-[#FF85C0] outline-none transition-colors";

function StepAbout({ form, update }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Full Name" required>
        <input
          type="text"
          value={form.full_name}
          onChange={(e) => update({ full_name: e.target.value })}
          className={inputCls}
          data-testid="bo-full_name"
        />
      </Field>
      <Field label="Age" required>
        <input
          type="number"
          min="18"
          max="99"
          value={form.age}
          onChange={(e) => update({ age: e.target.value })}
          className={inputCls}
          data-testid="bo-age"
        />
      </Field>
      <Field label="City" required>
        <input
          type="text"
          value={form.city}
          onChange={(e) => update({ city: e.target.value })}
          className={inputCls}
          data-testid="bo-city"
        />
      </Field>
      <Field label="State" required>
        <input
          type="text"
          value={form.state}
          onChange={(e) => update({ state: e.target.value })}
          className={inputCls}
          data-testid="bo-state"
        />
      </Field>
      <Field label="Phone Number" required>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => update({ phone: e.target.value })}
          className={inputCls}
          data-testid="bo-phone"
        />
      </Field>
      <Field label="Email Address" required>
        <input
          type="email"
          value={form.email}
          onChange={(e) => update({ email: e.target.value })}
          className={inputCls}
          data-testid="bo-email"
        />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Instagram / TikTok handles">
          <input
            type="text"
            placeholder="@yourhandle, @anotherhandle"
            value={form.socials}
            onChange={(e) => update({ socials: e.target.value })}
            className={inputCls}
            data-testid="bo-socials"
          />
        </Field>
      </div>
    </div>
  );
}

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex gap-2" data-testid={testid}>
    {["Yes", "No"].map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`flex-1 px-4 py-2.5 font-accent text-[11px] tracking-[0.22em] border transition-colors ${
          value === opt
            ? "border-[#FF85C0] bg-[#FF85C0]/10 text-[#FF85C0]"
            : "border-white/15 text-white/65 hover:text-white hover:border-white/35"
        }`}
      >
        {opt.toUpperCase()}
      </button>
    ))}
  </div>
);

function StepExperience({ form, update, toggleInterest }) {
  return (
    <div className="space-y-5">
      <Field label="Are you currently a travel agent?" required>
        <YesNo
          value={form.is_travel_agent}
          onChange={(v) => update({ is_travel_agent: v })}
          testid="bo-is_travel_agent"
        />
      </Field>
      <Field label="Why do you want to join Booked & Outside?" required>
        <textarea
          rows={4}
          value={form.why_join}
          onChange={(e) => update({ why_join: e.target.value.slice(0, 1500) })}
          className={inputCls + " resize-y"}
          data-testid="bo-why_join"
        />
      </Field>
      <Field label="What makes your personality good for reality TV?" required>
        <textarea
          rows={3}
          value={form.reality_tv_fit}
          onChange={(e) => update({ reality_tv_fit: e.target.value.slice(0, 1500) })}
          className={inputCls + " resize-y"}
          data-testid="bo-reality_tv_fit"
        />
      </Field>
      <Field label="Have you traveled before?" required>
        <YesNo
          value={form.traveled_before}
          onChange={(v) => update({ traveled_before: v })}
          testid="bo-traveled_before"
        />
      </Field>
      <Field label="Are you comfortable being filmed?" required>
        <YesNo
          value={form.comfortable_on_camera}
          onChange={(v) => update({ comfortable_on_camera: v })}
          testid="bo-comfortable_on_camera"
        />
      </Field>
      <Field label="Interested In (pick all that apply)" required>
        <div
          className="flex flex-wrap gap-2"
          data-testid="bo-interested_in"
        >
          {INTEREST_OPTIONS.map((opt) => {
            const active = form.interested_in.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggleInterest(opt)}
                className={`px-3 py-1.5 text-[11px] font-accent tracking-widest border transition-colors ${
                  active
                    ? "border-[#FF85C0] bg-[#FF85C0]/10 text-[#FF85C0]"
                    : "border-white/15 text-white/65 hover:text-white hover:border-white/35"
                }`}
              >
                {opt.toUpperCase()}
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function StepReady({ form, update }) {
  const items = [
    { k: "q_training_willing", label: "Are you willing to complete training to become a travel agent?", opts: ["Yes", "No"] },
    { k: "q_serious_about_business", label: "Are you serious about building a travel business and personal brand?", opts: ["Yes", "No"] },
    { k: "q_camera_active", label: "Are you willing to actively participate on camera while learning and growing?", opts: ["Yes", "No"] },
    { k: "q_open_to_networking", label: "Are you open to networking, destination travel, and group experiences?", opts: ["Yes", "No"] },
    { k: "q_commitment_level", label: "How committed are you to becoming successful in the travel industry?", opts: ["Just exploring", "Somewhat serious", "Fully committed"] },
  ];
  return (
    <div className="space-y-6">
      {/* Departure-board style banner */}
      <div className="relative border border-white/10 bg-black/55 backdrop-blur-md p-5 sm:p-6 overflow-hidden">
        <DepartureBoardFlicker />
        <div className="relative">
          <div className="font-accent text-[10px] tracking-[0.32em] text-[#5BA8FF] mb-2">
            / READINESS · NOW BOARDING
          </div>
          <h3
            className="font-heading uppercase font-black text-2xl sm:text-3xl tracking-tight"
            style={{ color: "#F6EBD4" }}
          >
            This is more than a vacation.
          </h3>
          <p className="mt-3 text-white/75 text-sm leading-relaxed">
            We are looking for people who are truly ready to learn, grow,
            travel, build relationships, and become successful travel
            entrepreneurs while being featured on an upcoming reality series.
          </p>
        </div>
      </div>

      {items.map((it) => (
        <Field key={it.k} label={it.label} required>
          <div className="flex flex-wrap gap-2" data-testid={`bo-${it.k}`}>
            {it.opts.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => update({ [it.k]: opt })}
                className={`px-3.5 py-2 font-accent text-[11px] tracking-[0.22em] border transition-colors ${
                  form[it.k] === opt
                    ? "border-[#FF85C0] bg-[#FF85C0]/10 text-[#FF85C0]"
                    : "border-white/15 text-white/65 hover:text-white hover:border-white/35"
                }`}
              >
                {opt.toUpperCase()}
              </button>
            ))}
          </div>
        </Field>
      ))}
    </div>
  );
}

function DepartureBoardFlicker() {
  const cells = Array.from({ length: 24 });
  return (
    <div
      className="absolute inset-0 grid grid-cols-12 gap-px opacity-25 pointer-events-none"
      aria-hidden
    >
      {cells.map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0.15 }}
          animate={{ opacity: [0.15, 0.55, 0.2, 0.45, 0.15] }}
          transition={{
            duration: 3 + (i % 4),
            repeat: Infinity,
            delay: (i * 0.13) % 2,
            ease: "easeInOut",
          }}
          className="bg-[#5BA8FF] h-1.5 self-end"
        />
      ))}
    </div>
  );
}

function StepMedia({ form, update, photos, setPhotos }) {
  const inputRef = useRef(null);
  const onFiles = (files) => {
    const arr = Array.from(files || []).slice(0, 5 - photos.length);
    Promise.all(
      arr.map(
        (f) =>
          new Promise((res, rej) => {
            if (!f.type.startsWith("image/")) return rej("not image");
            const r = new FileReader();
            r.onload = () => res({ dataUrl: r.result });
            r.onerror = rej;
            r.readAsDataURL(f);
          }),
      ),
    )
      .then((added) => setPhotos((p) => [...p, ...added]))
      .catch(() => {
        /* ignore */
      });
  };
  return (
    <div className="space-y-5">
      <Field label="Upload Photos (1-5)" required>
        <div
          className="border-2 border-dashed border-white/15 hover:border-[#FF85C0]/60 bg-black/30 p-6 text-center cursor-pointer transition-colors"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onFiles(e.dataTransfer.files);
          }}
          data-testid="bo-photo-dropzone"
        >
          <Upload className="w-5 h-5 mx-auto text-[#FF85C0] mb-2" />
          <div className="font-accent text-[11px] tracking-[0.28em] text-white/70">
            DRAG &amp; DROP OR CLICK TO UPLOAD
          </div>
          <div className="text-[10px] text-white/40 mt-1">
            Up to 5 photos · {photos.length}/5 uploaded
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => onFiles(e.target.files)}
            data-testid="bo-photo-input"
          />
        </div>
        {photos.length > 0 && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square border border-white/10">
                <img
                  src={p.dataUrl}
                  alt="upload"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() =>
                    setPhotos((arr) => arr.filter((_, idx) => idx !== i))
                  }
                  className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white/80 hover:bg-red-500/70 inline-flex items-center justify-center"
                  data-testid={`bo-photo-remove-${i}`}
                  aria-label="Remove photo"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Field>
      <Field label="Introduction Video — paste a link (YouTube / Vimeo / Drive)">
        <input
          type="url"
          placeholder="https://…"
          value={form.video_url}
          onChange={(e) => update({ video_url: e.target.value })}
          className={inputCls}
          data-testid="bo-video_url"
        />
      </Field>
      <div className="text-[11px] text-white/40 italic">
        Heads up: pressing <strong>Start My Journey</strong> sends your
        application instantly. We'll be in touch via the email above.
      </div>
    </div>
  );
}

function ThankYou() {
  return (
    <div className="text-center py-10" data-testid="booked-thanks">
      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[#FF2D9C] to-[#5BA8FF] flex items-center justify-center mb-6">
        <Plane className="w-7 h-7 text-white" />
      </div>
      <div className="font-accent text-[11px] tracking-[0.32em] text-[#FF85C0] mb-3">
        APPLICATION RECEIVED · WHEELS UP
      </div>
      <h2
        className="font-heading uppercase font-black text-4xl md:text-5xl leading-[0.95]"
        style={{ color: "#F6EBD4" }}
      >
        You're on the manifest.
      </h2>
      <p className="mt-5 max-w-lg mx-auto text-white/70 leading-relaxed">
        We've received your application for Booked &amp; Outside. Our
        production team reviews every submission personally — if you're a
        fit, you'll hear from us by email shortly.
      </p>
      <a
        href="/"
        className="mt-8 inline-flex items-center gap-2 border border-white/20 text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:bg-white/5 transition-colors"
      >
        Back to AutoMotoVille <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

// ============================================================
// Final CTA + Footer
// ============================================================
function FinalCTA({ onApply }) {
  return (
    <section
      className="relative px-6 py-28 md:py-36 overflow-hidden"
      data-testid="booked-final-cta"
    >
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 30%, rgba(255,45,156,0.30), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(91,168,255,0.25), transparent 55%)",
        }}
      />
      <div className="relative max-w-3xl mx-auto text-center">
        <div className="font-accent text-[11px] tracking-[0.32em] text-[#5BA8FF] mb-4">
          / 07 — FINAL CALL
        </div>
        <h2
          className="font-heading uppercase font-black text-5xl md:text-7xl leading-[0.95]"
          style={{ color: "#F6EBD4" }}
        >
          The world <br />
          is{" "}
          <span
            style={{
              background: "linear-gradient(90deg,#FF2D9C 0%,#FF85C0 100%)",
              WebkitBackgroundClip: "text",
              color: "transparent",
              backgroundClip: "text",
            }}
          >
            outside.
          </span>
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-white/75 leading-relaxed">
          If you're ready to build, travel, network, and experience the
          culture differently… this is your moment.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={onApply}
            data-testid="booked-final-apply"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#5BA8FF] text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:scale-[1.03] transition-transform"
          >
            <Sparkles className="w-4 h-4" /> Apply Now
          </button>
          <a
            href="/#newsletter"
            data-testid="booked-final-newsletter"
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:bg-white/5 transition-colors"
          >
            Join the Mailing List
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-6 pt-10 pb-32 sm:pb-10 border-t border-white/5">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="font-accent text-[10px] tracking-[0.32em] text-white/40">
          AUTOMOTOVILLE ORIGINAL · BOOKED &amp; OUTSIDE
        </div>
        <div className="flex items-center gap-3 text-xs text-white/55">
          <a
            href="mailto:casting@automotoville.com"
            className="hover:text-[#FF85C0]"
            data-testid="booked-footer-casting"
          >
            casting@automotoville.com
          </a>
          <span className="text-white/15">·</span>
          <a
            href="mailto:prezbosslady@automotoville.com"
            className="hover:text-[#5BA8FF]"
            data-testid="booked-footer-prez"
          >
            prezbosslady@automotoville.com
          </a>
        </div>
      </div>
    </footer>
  );
}
